/**
 * Lightweight analytics helper.
 *
 * The app loads GA4 in index.html. Everything else used to be invisible, which
 * made it impossible to tell which tools produced signups. Keep event names and
 * properties stable so reports can be built on top of them.
 */

type AnalyticsValue = string | number | boolean | null | undefined;
export type AnalyticsProps = Record<string, AnalyticsValue>;

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export function track(event: string, props: AnalyticsProps = {}): void {
  if (typeof window === 'undefined') return;

  const payload = {
    ...props,
    // A single timestamp makes it easy to join events client-side when needed.
    event_timestamp: new Date().toISOString(),
  };

  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', event, payload);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
  } catch {
    // Analytics must never break the product.
  }

  if (import.meta.env.DEV) {
    // Keeps local verification possible without opening GA realtime.
    console.debug('[analytics]', event, payload);
  }
}

/** Events used by the free-tool funnel. */
export const TOOL_EVENTS = {
  view: 'tool_view',
  firstInput: 'tool_first_input',
  resultReady: 'tool_result_ready',
  resumed: 'tool_resumed',
  draftSaved: 'tool_draft_saved',
  draftCleared: 'tool_draft_cleared',
  shareLinkCopied: 'tool_share_link_copied',
  exportClick: 'tool_export_click',
  exportCompleted: 'tool_export_completed',
  exportFollowUpView: 'tool_export_followup_view',
  exportFollowUpSubmit: 'tool_export_followup_submit',
  ctaClick: 'tool_cta_click',
  signupStart: 'tool_signup_start',
  signupComplete: 'tool_signup_complete',
  orgCreated: 'tool_org_created',
  resumeClick: 'tool_draft_resume_click',
  claimSubmit: 'tool_claim_submit',
} as const;
